import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList, ViewChild } from '@angular/core';
import { ExperimentalFeaturesService } from '@ca/core/services';
import { DynamicTabsDirective } from '../../directives/dynamic-tabs.directive';
import { TabComponent } from '../tab/tab.component';

@Component({
    selector: 'ca-tabset',
    templateUrl: './tabset.component.html',
    styleUrls: ['./tabset.component.scss'],
})
export class TabsetComponent implements AfterContentInit {
    dynamicTabs: TabComponent[] = [];

    @ContentChildren(TabComponent) tabs: QueryList<TabComponent>;

    @ViewChild(DynamicTabsDirective) dynamicTabPlaceholder: DynamicTabsDirective;

    /**
     * in case of using 'selectTab' to set a default tab, we might want to skip emitting 'onTabsChanged' event
     */
    @Input() autoActivateFirstTab = true;
    @Input() tabsWidth = 150;
    @Input() shouldBackButtonBeShown = true;

    @Output() onTabsChanged = new EventEmitter<string>();
    @Output() onButtonClicked = new EventEmitter<void>();

    selectedTab: string;
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    get isBackButtonShown(): boolean {
        return this.shouldBackButtonBeShown && this.onButtonClicked.observers.length > 0;
    }

    constructor(private experimentalFeaturesService: ExperimentalFeaturesService) {}

    ngAfterContentInit(): void {
        if (this.autoActivateFirstTab) {
            this.activateFirstTabIfNoTabIsActive();
        }
    }

    selectTab(tabId: string, ignoreEvent = false): void {
        const tab: TabComponent | undefined = this.findTabById(tabId);

        if (tab != null) {
            this.deactivateAllTabs();
            tab.active = true;
            this.selectedTab = tab.tabId;
            if (!ignoreEvent) {
                this.onTabsChanged.emit(tab.tabId);
            }
        }
    }

    getActiveTabId(): string | undefined {
        let activeTabId: string | undefined;

        // First, search in Dynamic tabs. If not found, search in this.tabs
        const activeTabInDynamicTabs: TabComponent | undefined = this.dynamicTabs.find(t => t.active === true);
        if (activeTabInDynamicTabs != null) {
            activeTabId = activeTabInDynamicTabs.tabId;
        } else {
            // If not found in dynamic tabs, search in tabs
            const activeTab: TabComponent | undefined = this.tabs.toArray().find(t => t.active === true);
            if (activeTab != null) {
                activeTabId = activeTab.tabId;
            }
        }

        return activeTabId;
    }

    closeTab(tab: TabComponent): void {
        for (let i = 0; i < this.dynamicTabs.length; i++) {
            if (this.dynamicTabs[i] === tab) {
                // remove the tab from our array
                this.dynamicTabs.splice(i, 1);

                // destroy our dynamically created component again
                const viewContainerRef = this.dynamicTabPlaceholder.viewContainer;
                viewContainerRef.remove(i);

                // set tab index to 1st one
                this.selectTab(this.tabs.first.tabId);
                break;
            }
        }
    }

    private activateFirstTabIfNoTabIsActive(): void {
        const activeTabs = this.tabs.filter(tab => tab.active);
        if (activeTabs.length === 0 && this.tabs.first) {
            this.selectTab(this.tabs.first.tabId);
        }
    }

    private deactivateAllTabs(): void {
        this.tabs.toArray().forEach(t => (t.active = false));
        this.dynamicTabs.forEach(t => (t.active = false));
    }

    private findTabById(tabId: string): TabComponent | undefined {
        return this.tabs.toArray().find((tab: TabComponent) => tab.tabId === tabId);
    }
}
